import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUpdateInvoiceForwardingSettings, useUser } from '~/api/user';
import { Alert, Blocker, Button, Menu } from '~/components';
import { Field, Form, Input, Select } from '~/components/Form';
import { EMAIL } from '~/constants/regex';
import { useDialog } from '~/hooks/useDialog';
import { clsx } from '~/styles/utils';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type { AutoForwardingFormType, AutoForwardingFormProps as Props } from './types';

import {
  actionsStyle,
  dataRowStyle,
  fieldStyle,
  mobileLabelStyle,
  rowExtraAlertStyle,
  rowExtraStyle,
  rowStyle,
} from './AutoForwarding.css';
import { DeleteAutoForwardingModal } from './DeleteAutoForwardingModal';

const frequencies = ['instant', 'monthly', 'quarterly'] as const;

export const AutoForwardingForm = ({ disableForm, enableForm, invoiceForwardingSettings, isEnabled, isLocked }: Props) => {
  const { data: user } = useUser();
  const mutation = useUpdateInvoiceForwardingSettings();

  const { t } = useTranslation(['common', 'settings', 'validation']);

  const frequencyItems = frequencies.map((frequency) => ({
    label: t(`settings:fields.forwarding.frequency.options.${frequency}`),
    value: frequency,
  }));

  const isEdit = !!invoiceForwardingSettings;
  const deleteDialog = useDialog<NonNullable<Props['invoiceForwardingSettings']>>();

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    watch,
  } = useForm<AutoForwardingFormType>({
    defaultValues: {
      receiverEmail: isEdit ? invoiceForwardingSettings.receiverEmail : '',
      frequency: isEdit ? invoiceForwardingSettings.frequency : 'instant',
      platformEmail: isEdit ? invoiceForwardingSettings.platformEmail : '',
    },
  });

  const watchReceiverEmail = watch('receiverEmail').toLowerCase();
  const isAccountable = ['revenue@accountable.eu', 'inkomsten@accountable.eu'].includes(watchReceiverEmail);
  const isBilltobox = watchReceiverEmail.includes('billtobox');
  const isDexxter = /@(inbound|expense)\.dexxter\.be/.test(watchReceiverEmail);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await mutation.mutateAsync({
        entries: isEdit
          ? user.invoiceForwardingSettings.map((entry) => (entry.id === invoiceForwardingSettings.id ? data : entry))
          : [...user.invoiceForwardingSettings, data],
      });

      reset(data);
      disableForm();
      toast.success({ description: t('settings:alerts.integrations.successForwardingEmailLinked') });
    } catch {
      toast.danger({ description: t('common:error') });
    }
  });

  const onCancel = () => {
    reset();
    disableForm();
  };

  return (
    <>
      {isEnabled && <Blocker isBlocked={isDirty} message={t('settings:integrations.forwarding.blocker')} />}

      <Form className={clsx(rowStyle, dataRowStyle)} onSubmit={onSubmit}>
        <Controller
          control={control}
          name="receiverEmail"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
            <Field.Root className={fieldStyle} invalid={invalid} name={name}>
              <Field.Label className={mobileLabelStyle}>{t('settings:fields.email.label')}</Field.Label>

              <Input
                autoComplete="off"
                autoFocus={isEnabled && !isEdit}
                disabled={!isEnabled}
                onBlur={onBlur}
                onValueChange={onChange}
                placeholder={t('settings:fields.email.placeholder')}
                ref={ref}
                type="email"
                value={value}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
            maxLength: { value: 255, message: t('validation:maxLength', { attribute: t('settings:fields.email.label'), max: 255 }) },
            pattern: { value: EMAIL, message: t('validation:email.invalid') },
          }}
        />

        <Controller
          control={control}
          name="frequency"
          render={({ field: { name, onChange, value } }) => (
            <Field.Root className={fieldStyle} name={name}>
              <Field.Label className={mobileLabelStyle} infotip={t('settings:fields.forwarding.frequency.tooltip')}>
                {t('settings:fields.forwarding.frequency.label')}
              </Field.Label>

              <Select.Root
                disabled={!isEnabled}
                items={frequencyItems}
                onValueChange={(newValue) => newValue && onChange(newValue)}
                value={value}
              >
                <Select.Button />

                <Select.List>
                  {frequencyItems.map((item) => (
                    <Select.Item key={item.value} value={item.value}>
                      {item.label}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>
            </Field.Root>
          )}
        />

        <div className={actionsStyle}>
          {isEnabled ? (
            <>
              <Button aria-label={t('common:actions.cancel')} emphasis="tertiary" icon="CloseSmall" onClick={onCancel} />

              <Button aria-label={t('common:save')} icon="Save" loading={mutation.isPending} type="submit" />
            </>
          ) : (
            isEdit && (
              <Menu.Root>
                <Menu.Trigger
                  disabled={isLocked}
                  render={<Button aria-label={t('common:actions.openMenu')} emphasis="tertiary" icon="MoreVert" />}
                />

                <Menu.List>
                  <Menu.Item icon="Edit" onClick={() => enableForm(invoiceForwardingSettings.id)}>
                    {t('common:actions.edit')}
                  </Menu.Item>

                  <Menu.Item icon="Trash" onClick={() => deleteDialog.open(invoiceForwardingSettings)}>
                    {t('common:actions.delete')}
                  </Menu.Item>
                </Menu.List>
              </Menu.Root>
            )
          )}
        </div>

        {(isAccountable || (isEnabled && isBilltobox)) && (
          <div className={rowExtraStyle}>
            {isAccountable && (
              <Controller
                control={control}
                name="platformEmail"
                render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid } }) => (
                  <Field.Root className={fieldStyle} invalid={invalid} name={name}>
                    <Field.Label infotip={t('settings:fields.forwarding.platformEmail.tooltip')}>
                      {t('settings:fields.forwarding.platformEmail.label')}
                    </Field.Label>

                    <Input disabled={!isEnabled} onBlur={onBlur} onValueChange={onChange} ref={ref} type="email" value={value} />

                    <Field.Error match={!!error}>{error?.message}</Field.Error>
                  </Field.Root>
                )}
                rules={{
                  required: { value: true, message: t('validation:required') },
                  validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
                  maxLength: { value: 255, message: t('validation:maxLength', { attribute: t('settings:fields.email.label'), max: 255 }) },
                  pattern: { value: EMAIL, message: t('validation:email.invalid') },
                }}
              />
            )}

            {isEnabled && isBilltobox && (
              <Alert announce className={rowExtraAlertStyle} icon="Info" status="info">
                {t('settings:fields.forwarding.alerts.billtobox')}
              </Alert>
            )}
          </div>
        )}
      </Form>

      {deleteDialog.isOpen && (
        <DeleteAutoForwardingModal
          invoiceForwardingSetting={deleteDialog.data}
          isDexxter={isDexxter}
          onClose={deleteDialog.close}
          onDeleteSuccess={disableForm}
        />
      )}
    </>
  );
};
